/**
 * Runtime build & reload: the session's tool-registry assembly and the self-modification-safe
 * extension reload path. Owns building the base tool definitions, wrapping them into the live tool
 * registry (`_refreshToolRegistry`), constructing the {@link ExtensionRunner} for a rebuilt runtime
 * (`_buildRuntime`), and the repo's self-modification safety crown jewel: the preflight-guarded
 * `reload()` with its snapshot / doctor / commit-or-rollback sequence, plus the single-extension
 * live load/unload/reconcile operations that rebuild the runtime in place.
 *
 * Extracted verbatim from agent-session.ts (god-file decomposition). Owns ONLY the tool-registry
 * state — the base tool definitions, the wrapped tool registry, the definition registry, and the
 * per-tool prompt snippet/guideline maps. Everything else the build & reload touch — the live
 * {@link ExtensionRunner}, the agent's tool/system-prompt state, the base system prompt, the
 * resource loader, the session/settings managers, the model registry, the profile tool filter and
 * its warning sinks, the requested-active-tool-names request, the memory subsystem, and the many
 * host callbacks the rebuilt tools/runtime are wired to — is reached through narrow deps accessors
 * that read and write the SAME storage the host owns today.
 *
 * Snapshot-ownership boundary (deliberate, load-bearing for reload safety): the reload snapshot
 * spans state owned by OTHER collaborators — the extension runner and its ref, `agent.state.tools`
 * / `agent.state.systemPrompt`, and `_baseSystemPrompt`. Those are captured and restored through
 * {@link RuntimeBuilderDeps} get/set accessors so save/restore mutate exactly the host/agent fields
 * they mutated before extraction; only the five tool-registry maps are captured by direct field
 * reference here (this builder owns them). The `_extensionRunnerRef.current` update folds into
 * {@link RuntimeBuilderDeps.setExtensionRunner} (the same field-then-ref pattern the host used at
 * every assignment site), so no observer runs between the two writes.
 *
 * Host-binding boundary (deliberate): `_bindExtensionCore` — which exposes the SESSION's own public
 * surface (sendMessage, setModel, compact, abort, reload, …) to the extension runner — stays
 * host-side and is invoked from {@link buildRuntime} via {@link RuntimeBuilderDeps.bindExtensionCore};
 * it is host identity, not build logic, and moving it would only re-export ~30 host methods through
 * deps to hand them straight back. The host keeps one-line delegations for the public reload /
 * load / unload / reconcile API and for `getAllTools` / `getToolDefinition`, and a private
 * `_refreshToolRegistry` delegation for its internal callers.
 */

import { existsSync } from "node:fs";
import { isAbsolute, join } from "node:path";
import type { Agent, AgentContext, AgentMessage, AgentTool, ThinkingLevel } from "@caupulican/pi-agent-core";
import type { SessionManager } from "@caupulican/pi-agent-core/node";
import type { Api, Model, Usage } from "@caupulican/pi-ai";
import { getShellEnv } from "../utils/shell.ts";
import {
	bitwardenSecretsManagerCoordinationFile,
	managedSecretEnvDir,
	resourceDir,
	secretVaultFile,
} from "./agent-paths.ts";
import type {
	IsolatedCompletionOptions,
	IsolatedCompletionResult,
	WorkerDelegationRunOutcome,
} from "./agent-session-contracts.ts";
import { deriveCompositeChildEnvelope, wrapToolWithCapabilityEnvelopeGate } from "./autonomy/composite-tool-gate.ts";
import type { CapabilityEnvelope, WorkerClaim } from "./autonomy/contracts.ts";
import { isPathWithinEnvelope, wrapToolWithEnvelopeScope } from "./autonomy/envelope-enforcement.ts";
import type { LaneRecord } from "./autonomy/lane-tracker.ts";
import { buildWorkerSessionPrivatePathEnvelope } from "./autonomy/worker-session-private-scope.ts";
import { isCompletedBackgroundToolEvidence } from "./background-tool-task-controller.ts";
import type { ArtifactStore } from "./context/context-artifacts.ts";
import type { MemoryPromptInclusionReport, MemoryRetrievalDiagnostics } from "./context/memory-diagnostics.ts";
import type { ContextGcReport } from "./context-gc.ts";
import { DEFAULT_ACTIVE_TOOL_NAMES, mapToolNamesForPlatform } from "./default-tool-surface.ts";
import { acknowledgeWorkerClaimReview } from "./delegation/session-worker-claim.ts";
import type { WorkerAgentControlPort } from "./delegation/worker-agent-control.ts";
import type { WorkerDelegationRequest } from "./delegation/worker-delegation-request.ts";
import { execCommand } from "./exec.ts";
import type { ExtensionImportAuthority } from "./extension-import-authority.ts";
import { createCoreDiagnosticsToolDefinitions } from "./extensions/builtin.ts";
import {
	type ContextUsage,
	type Extension,
	type ExtensionCommandContextActions,
	type ExtensionErrorListener,
	ExtensionRunner,
	type ExtensionUIContext,
	type ShutdownHandler,
	type ToolDefinition,
	type ToolInfo,
	wrapRegisteredTools,
} from "./extensions/index.ts";
import { disposeExtensionEventSubscriptions } from "./extensions/lifecycle.ts";
import { emitSessionShutdownEvent } from "./extensions/runner.ts";
import type { GoalStateRevision } from "./goals/goal-lifecycle.ts";
import type { GoalState } from "./goals/goal-state.ts";
import type { OpenTaskStepRef } from "./goals/goal-tool-core.ts";
import { GOAL_LIFECYCLE_TOOL_NAMES, LEGACY_GOAL_TOOL_NAME } from "./goals/goal-tool-names.ts";
import { createImprovementLoopTool } from "./improvement-loop.ts";
import type { MemoryManager } from "./memory/memory-manager.ts";
import type { MemoryControllerReloadSnapshot } from "./memory-controller.ts";
import type { LaneWorkerRefusal } from "./model-capability.ts";
import type { ModelRegistry } from "./model-registry.ts";
import { resolveCliModel } from "./model-resolver.ts";
import { evaluateSurfaceFitness } from "./model-router/fitness-gate.ts";
import { resolveModelToolProtocol } from "./model-tool-protocol.ts";
import { ModelAdaptationStore } from "./models/adaptation-store.ts";
import { FitnessStore } from "./models/fitness-store.ts";
import type { OrchestrationProfile, WorkerResultContract } from "./orchestration/contracts.ts";
import type { TaskProfileWriterPort } from "./orchestration/task-profile-writer.ts";
import { resolvePipelineDefinitionForRun } from "./pipelines/discover.ts";
import { resolveCurrentProjectPipelineRun } from "./pipelines/run-state.ts";
import { isPipelineRunActive, type PipelineRun } from "./pipelines/types.ts";
import type { ProfileFilterReloadSnapshot } from "./profile-filter-controller.ts";
import { describeInFlightWorkUnit, getInFlightWorkUnits } from "./reload-blockers.ts";
import type { ModelFitnessReport } from "./research/model-fitness.ts";
import type { ResourceLoader } from "./resource-loader.ts";
import { ScoutController } from "./scout-controller.ts";
import { BitwardenCredentialStorageRouter } from "./secrets/bitwarden-credential-storage-router.ts";
import {
	type CredentialExposureBoundary,
	wrapToolWithCredentialExposureGuard,
} from "./secrets/credential-exposure-guard.ts";
import {
	discoverMachineCredentialSession,
	getMachineCredentialBootstrapFiles,
	getMachineCredentialDiscoveryRoots,
} from "./secrets/credential-machine-session.ts";
import { CredentialManager } from "./secrets/credential-manager.ts";
import { resolveCredentialProject } from "./secrets/credential-project.ts";
import { discoverCredentialMigrationSources } from "./secrets/credential-source-discovery.ts";
import type { SessionImageStore } from "./session-image-store.ts";
import { getSessionRole, isWorkerSession, WORKER_FORBIDDEN_TOOLS } from "./session-role.ts";
import {
	matchesResourceProfilePattern,
	type ResourceProfileFilterSettings,
	type SettingsManager,
} from "./settings-manager.ts";
import type { SkillVaultController } from "./skill-vault.ts";
import { createSyntheticSourceInfo, type SourceInfo } from "./source-info.ts";
import { projectOpenTaskSteps } from "./tasks/task-projection.ts";
import type { TaskStepsState } from "./tasks/task-state.ts";
import { resolveCurrentToolRepairSettings } from "./tool-repair-settings.ts";
import { runReflexInterpreterCompletion } from "./toolkit/reflex-interpreter.ts";
import { executeToolkitScript } from "./toolkit/script-runner.ts";
import { createAskQuestionToolDefinition } from "./tools/ask-question.ts";
import { createContextScoutToolDefinition } from "./tools/context-scout.ts";
import { createDelegateToolDefinition } from "./tools/delegate.ts";
import { FileMutationIntentController } from "./tools/file-mutation-intent.ts";
import { createFindTool } from "./tools/find.ts";
import { createGoalLifecycleToolDefinitions, createGoalToolDefinition, type GoalToolInput } from "./tools/goal.ts";
import { createGrepTool } from "./tools/grep.ts";
import { createModelFitnessToolDefinition } from "./tools/model-fitness.ts";
import { resolveToCwd } from "./tools/path-utils.ts";
import { createPipelineToolDefinition } from "./tools/pipeline.ts";
import { createReadTool } from "./tools/read.ts";
import { createRunProcessToolDefinition } from "./tools/run-process.ts";
import { createRunToolkitScriptToolDefinition } from "./tools/run-toolkit-script.ts";
import { createSecretStoreToolDefinition } from "./tools/secret-store.ts";
import { disposeShellExecutionSession } from "./tools/shell-execution-session.ts";
import { createSkillVaultToolDefinition } from "./tools/skill.ts";
import { createTaskStepsToolDefinition } from "./tools/task-steps.ts";
import { dispatchTmuxWorker } from "./tools/tmux-dispatch.ts";
import {
	allToolNames,
	createToolDefinitionWithRuntime,
	type ToolDef,
	type ToolDefinitionOptions,
} from "./tools/tool-definition-factory.ts";
import { createToolDefinitionFromAgentTool } from "./tools/tool-definition-wrapper.ts";
import { createToolTaskToolDefinition, type ToolTaskDependencies } from "./tools/tool-task.ts";
import { createWorktreeSyncToolDefinition } from "./tools/worktree-sync.ts";
import { countFileLinesSync } from "./util/bounded-file.ts";
import { createLane } from "./worktree-sync/git-engine.ts";
import { WorktreeLaneGate } from "./worktree-sync/lane-gate.ts";
import { buildWorktreeSyncEngineDeps, getBoundWorktreeLaneKey } from "./worktree-sync/runtime.ts";

interface ToolDefinitionEntry {
	definition: ToolDefinition;
	sourceInfo: SourceInfo;
}

/**
 * Is `toolCallId` a real, answered tool call on `sessionManager`'s active branch? A
 * toolCallId is "real" iff a toolResult message on that branch responded to it -- the same
 * toolResult/toolCallId match `context-pipeline.ts`'s `_buildSessionEntryIdLookup` uses, and
 * branch-scoped (via `getBranch()`) so a sibling branch's tool calls never count. Exported (pure,
 * no `this`) so the goal tool's `hasToolCallId` wiring below is directly testable against a real
 * `SessionManager` without constructing the whole `RuntimeBuilder`.
 */
export function hasAnsweredToolCallOnBranch(sessionManager: SessionManager, toolCallId: string): boolean {
	return sessionManager
		.getBranch()
		.some(
			(entry) =>
				entry.type === "message" && entry.message.role === "toolResult" && entry.message.toolCallId === toolCallId,
		);
}

/**
 * Goal-tool adapter over the canonical open-task projection. Exported (pure, no `this`) so the
 * production `getOpenTaskSteps` wiring below remains directly testable without another filter.
 */
export function deriveOpenTaskStepRefs(taskStepsState: TaskStepsState | undefined): OpenTaskStepRef[] {
	return projectOpenTaskSteps(taskStepsState).map((step) => ({
		id: step.id,
		content: step.content,
		...(step.requirementIds?.length ? { requirementIds: step.requirementIds } : {}),
		...(step.evidence?.length ? { evidence: step.evidence } : {}),
		...(step.pipelineRunId ? { pipelineRunId: step.pipelineRunId } : {}),
		...(step.pipelineStageId ? { pipelineStageId: step.pipelineStageId } : {}),
	}));
}

/** Bind goal-selected requirement identity as runtime metadata, outside the model-facing delegate schema. */
export function createGoalWorkerDelegationRequest(args: {
	requirementId: string;
	instructions: string;
}): WorkerDelegationRequest {
	return {
		instructions: args.instructions,
		taskContext: {
			requirementIds: [args.requirementId],
			dependsOnTaskIds: [],
			acceptanceCriterionIds: [],
			resourcePointerIds: [],
		},
	};
}

interface ReloadRuntimeSnapshot {
	extensionRunner: ExtensionRunner;
	settings: ReturnType<SettingsManager["createReloadSnapshot"]>;
	modelRegistry: ReturnType<ModelRegistry["createReloadSnapshot"]>;
	model: Model<Api>;
	thinkingLevel: ThinkingLevel;
	sessionLeafId: string | null;
	toolProfileFilter: Required<ResourceProfileFilterSettings> | undefined;
	requestedActiveToolNames: string[] | undefined;
	memory: MemoryControllerReloadSnapshot;
	profileFilter: ProfileFilterReloadSnapshot;
	unboundToolGrantWarnings: string[];
	baseToolDefinitions: Map<string, ToolDefinition>;
	toolRegistry: Map<string, AgentTool>;
	toolDefinitions: Map<string, ToolDefinitionEntry>;
	toolPromptSnippets: Map<string, string>;
	toolPromptGuidelines: Map<string, string[]>;
	agentTools: AgentTool[];
	agentSystemPrompt: string;
	baseSystemPrompt: string;
}

export interface RuntimeBuilderDeps {
	/** Live agent — the snapshot/doctor read and restore its `state.tools` / `state.systemPrompt`. */
	getAgent(): Agent;
	/** Workspace root, passed to the tool-definition factory and the extension runner. */
	getCwd(): string;
	/** Per-agent persistent shell session key; stable across reloads so the shell survives them. */
	getShellSessionKey(): string;
	/** Agent state root, including the host-keyed fitness store. */
	getAgentDir(): string;
	/** Session log, passed to the extension runner. */
	getSessionManager(): SessionManager;
	/** Tool/shell/toolkit/resource settings + reload target (settingsManager.reload()). */
	getSettingsManager(): SettingsManager;
	/** Model registry, passed to the extension runner and profile model re-resolution. */
	getModelRegistry(): ModelRegistry;
	/** Session-scoped provider/model quota exhaustion guard. */
	isModelExhausted(model: Model<Api>): boolean;
	/** Extension/skill/prompt/theme discovery + the reload/commit/rollback generation swap. */
	getResourceLoader(): ResourceLoader;
	/** Host-owned transient skill lifecycle shared by tool execution and context projection. */
	getSkillVault(): SkillVaultController;

	/** Live extension runner (host-owned; pervasive). */
	getExtensionRunner(): ExtensionRunner;
	/** Store the extension runner AND update the Agent's mutable `_extensionRunnerRef.current` (both together). */
	setExtensionRunner(runner: ExtensionRunner): void;
	/** Base (extension-free) system prompt; captured/restored by the reload snapshot. */
	getBaseSystemPrompt(): string;
	setBaseSystemPrompt(prompt: string): void;

	/** SDK-provided plain tools, synthesized into the registry alongside built-ins. */
	getCustomTools(): ToolDefinition[];
	/** Optional plain-tool override; when set, the built-in factory + core diagnostics are skipped. */
	getBaseToolsOverride(): Record<string, AgentTool> | undefined;
	/** Pre-filter tool REQUEST (never the capability/profile-filtered active set) preserved across a rebuild. */
	getRequestedActiveToolNames(): string[] | undefined;
	setRequestedActiveToolNames(names: string[] | undefined): void;

	/** Resource-profile tool allow/block filter + the raw allow/exclude sets applied during registry build. */
	getToolProfileFilter(): Required<ResourceProfileFilterSettings> | undefined;
	setToolProfileFilter(filter: Required<ResourceProfileFilterSettings> | undefined): void;
	getAllowedToolNames(): Set<string> | undefined;
	getCapabilityEnvelope?(): CapabilityEnvelope | undefined;
	/** Immutable active orchestration profile; absent sessions never construct run_process. */
	getOrchestrationProfile?(): OrchestrationProfile | undefined;
	getExcludedToolNames(): Set<string> | undefined;
	/** Re-derive the profile tool filter from freshly reloaded settings (reload only). */
	deriveToolProfileFilter(): Required<ResourceProfileFilterSettings>;
	/** True when a tool/command name survives the active profile's allow/block + user allow/exclude. */
	isToolOrCommandAllowedByProfile(name: string): boolean;
	/** Import-boundary decision for an exact owner load or an automatic profile-driven load. */
	isExtensionPathAllowed(path: string, authority: ExtensionImportAuthority, baseDir?: string): boolean;
	/** Filter the loaded extensions through the active resource profile (records inert/denied warnings host-side). */
	filterExtensionsForRuntime(extensions: Extension[], explicitLiveExtensionPaths?: ReadonlySet<string>): Extension[];
	/** Sink for the unbound-profile-tool-grant warnings surfaced in /context. */
	setUnboundToolGrantWarnings(warnings: string[]): void;
	getUnboundToolGrantWarnings(): string[];
	/**
	 * Optional sink for delegate provider-prompt-guideline bounding diagnostics (a guideline dropped
	 * or truncated to fit the provider prompt budget), surfaced in /context alongside the other
	 * construction-time tool diagnostics above. Optional so embeddings/tests that hand-build
	 * RuntimeBuilderDeps without this seam keep working; the diagnostic is then silently dropped
	 * rather than required for correct delegate operation.
	 */
	setDelegatePromptGuidelineWarnings?(warnings: string[]): void;
	createProfileFilterReloadSnapshot(): ProfileFilterReloadSnapshot;
	restoreProfileFilterReloadSnapshot(snapshot: ProfileFilterReloadSnapshot): void;

	/** Currently-active tool names (reads agent.state.tools; the pre-filter fallback for a rebuild). */
	getActiveToolNames(): string[];
	/** Apply the recomputed active set (capability filter + required artifact/delegation companions live here). */
	setActiveToolsByName(toolNames: string[]): void;
	/** Normalize a tool's prompt snippet / guidelines through the system-prompt builder. */
	normalizePromptSnippet(text: string | undefined): string | undefined;
	normalizePromptGuidelines(guidelines: string[] | undefined): string[];

	/** Wire the session's own public surface into a freshly-built runner (host identity; stays host-side). */
	bindExtensionCore(runner: ExtensionRunner): void;
	/** Re-apply UI context / mode / command-context / error subscription to a runner. */
	applyExtensionBindings(runner: ExtensionRunner): void;
	/** Re-run resource discovery for the active extensions (reload only). */
	extendResourcesFromExtensions(reason: "startup" | "reload"): Promise<void>;
	/** Re-apply the active profile's model/thinking from reloaded settings (reload only). */
	reapplyActiveProfileModelSettings(): Promise<void>;
	/** Notify extensions-changed listeners after a single-extension live op. */
	notifyExtensionsChanged(): void;

	/** Session-scoped tool-output artifact store for artifact-producing tools and artifact_retrieve (gated on the profile). */
	getToolArtifactStore(): ArtifactStore;
	/** Session-owned slow-tool registry. Optional only for narrow RuntimeBuilder test/embedding seams. */
	getToolTaskDependencies?(): ToolTaskDependencies;
	/** Lazily resolve durable image storage only for a persisted/configured session. */
	getSessionImageStore(): Pick<SessionImageStore, "retainContent"> | undefined;

	/** Live memory manager — its provider tools join the registry. */
	getMemoryManager(): MemoryManager;
	/** Memory retrieval + prompt-inclusion diagnostics for the core diagnostics tool. */
	getMemoryAuditDiagnostics(): { retrieval: MemoryRetrievalDiagnostics; promptInclusion: MemoryPromptInclusionReport };
	/** Drop extension-contributed pending memory providers before a reload re-registers them. */
	clearPendingMemoryProviders(): void;
	createMemoryReloadSnapshot(): MemoryControllerReloadSnapshot;
	restoreMemoryReloadSnapshot(snapshot: MemoryControllerReloadSnapshot): void;
	/** (Re)derive the memory subsystem from reloaded settings/providers. */
	initializeMemory(): Promise<void>;

	/** Goal-tool state accessors. */
	getGoalStateSnapshot(): GoalState | undefined;
	saveGoalStateSnapshot(state: GoalState, expected?: GoalStateRevision): string;
	/** Authorize model-facing goal creation and its exact owner-requested token ceiling. */
	authorizeGoalStartFromTool?(
		input: Pick<GoalToolInput, "userGoal" | "tokenBudget">,
	): string | number | null | undefined;
	/** Native task-step state accessors. */
	getTaskStepsStateSnapshot(): TaskStepsState | undefined;
	saveTaskStepsStateSnapshot(state: TaskStepsState): string;
	getPipelineRunSnapshot(): PipelineRun | undefined;
	savePipelineRunSnapshot(run: PipelineRun): string;
	/** Context-gc report for the core diagnostics tool. */
	getContextGcReport(messages: AgentMessage[]): ContextGcReport;
	/** Non-blocking worker-delegation starter for the delegate tool. */
	startWorkerDelegation(
		request: WorkerDelegationRequest,
	): { started: false; skipReason: string } | { started: true; record: LaneRecord };
	workerAgentControl?: WorkerAgentControlPort & Partial<TaskProfileWriterPort>;
	getOrchestrationProfileCatalog(): Array<{ profileId: string; role: string; description: string }>;
	getWorkerLaneRecords(): LaneRecord[];
	getWorkerClaimSnapshots(): WorkerClaim[];
	getWorkerResult?(laneId: string): WorkerResultContract | undefined;
	/** Confirm a managed dispatch's caller-stable canonical lane id was registered durably before the
	 * goal binds it (`BackgroundLaneController.resolveManagedLaneId`). */
	resolveManagedLaneId(callerLaneId: string): string | undefined;
	/** Worker-delegation runner for SDK/test through-completion calls. */
	runWorkerDelegationOnce(request: WorkerDelegationRequest): Promise<WorkerDelegationRunOutcome>;
	/** Model-fitness probe for the model_fitness tool. `toolCallId` is the idempotency token
	 * for spawned-usage reportId — present only for the LLM tool-call path (see model-fitness.ts). */
	runModelFitness(args: {
		model: string;
		trials?: number;
		toolCallId?: string;
	}): Promise<{ started: true; model: string; report: ModelFitnessReport } | { started: false; skipReason: string }>;
	/** Fitness-gated reflex-brain model resolver (run_toolkit_script interpretation). */
	resolveCurationModelIfFit(): Model<Api> | undefined;
	/** One-shot, tool-less LLM call — the reflex-brain interpreter rides this. */
	runIsolatedCompletion(opts: IsolatedCompletionOptions): Promise<IsolatedCompletionResult>;
	/** Roll reflex-brain spend into spawned-usage accounting. `reportId` is REQUIRED: every
	 * caller derives a stable id from the work unit's identity so a retry cannot double-count. */
	addSpawnedUsage(
		usage: Usage,
		opts: { label?: string; sourceSessionId?: string; reportId: string },
	): string | undefined;
	/** Whether the CURRENT session model may drive a worktree-sync lane worker (`AgentSession.
	 * getLaneWorkerRefusal`) -- consulted BEFORE a goal→tmux dispatch so an ineligible model's
	 * dispatch is refused before any lane/pane side effect (`tools/tmux-dispatch.ts`'s
	 * `evaluateWorkerLaneRefusal`). `undefined` means eligible. */
	getLaneWorkerRefusal(): LaneWorkerRefusal | undefined;

	/** Post-rebuild doctor helpers (validate the rebuilt runtime can render a context). */
	createAgentContextSnapshot(): AgentContext;
	getContextUsage(): ContextUsage | undefined;

	/** Reload/live-op preflight refusal guards (identical refusal behavior; host-checked). */
	isStreaming(): boolean;
	isCompacting(): boolean;

	/** Extension bindings present — gate for re-emitting session_start on reload. */
	getExtensionUIContext(): ExtensionUIContext | undefined;
	getExtensionCommandContextActions(): ExtensionCommandContextActions | undefined;
	getExtensionShutdownHandler(): ShutdownHandler | undefined;
	getExtensionErrorListener(): ExtensionErrorListener | undefined;

	/**
	 * Stop any pi-spawned local (Ollama/Transformers/prism llama.cpp) runtime the host's
	 * LocalRuntimeController is holding open that no longer applies under the just-reloaded
	 * configuration — e.g. `LocalRuntimeController.reconcile(eligibleModels)`, with `eligibleModels`
	 * derived from whatever the host still routes to post-reload (foreground model + any configured
	 * router tier). Called ONLY after `_reloadOnce()` commits successfully — never on a rolled-back
	 * reload, since a rollback restores the PREVIOUS configuration and nothing became ineligible.
	 * Optional: a host that hasn't wired a LocalRuntimeController through here yet sees no behavior
	 * change — a local runtime this builder doesn't know about is simply left alone, never guessed at.
	 */
	reconcileLocalRuntimes?(): void;
}

interface RuntimeToolAccessPolicy {
	allowedToolNames: ReadonlySet<string> | undefined;
	toolProfileFilter: Required<ResourceProfileFilterSettings> | undefined;
	allows(name: string): boolean;
}

/**
 * Owns the tool-registry build and the self-modification-safe extension reload extracted from
 * {@link AgentSession}. See the module header for the snapshot-ownership and host-binding boundaries.
 */
export class RuntimeBuilder {
	// Tool registry for extension getTools/setTools
	private _toolRegistry: Map<string, AgentTool> = new Map();
	private _toolDefinitions: Map<string, ToolDefinitionEntry> = new Map();
	private _toolPromptSnippets: Map<string, string> = new Map();
	private _toolPromptGuidelines: Map<string, string[]> = new Map();
	private _baseToolDefinitions: Map<string, ToolDefinition> = new Map();
	/** Exact extensions the owner approved through the live-load API. Discovery never populates this set. */
	private readonly _explicitLiveExtensionPaths = new Set<string>();
	private _reloadPromise: Promise<void> | undefined;
	private _reloadRequested = false;
	private readonly _credentialManager: CredentialManager;
	private readonly _credentialExposureBoundary: CredentialExposureBoundary;
	private readonly _credentialBootstrapFiles: readonly string[];
	private readonly _credentialDiscoveryRoots: readonly string[];
	private readonly _fileMutationIntents: FileMutationIntentController;
	private readonly _workerSessionPrivatePathEnvelope: CapabilityEnvelope | undefined;

	private readonly deps: RuntimeBuilderDeps;

	constructor(deps: RuntimeBuilderDeps) {
		this.deps = deps;
		this._workerSessionPrivatePathEnvelope = isWorkerSession()
			? buildWorkerSessionPrivatePathEnvelope(deps.getCwd(), deps.getAgentDir())
			: undefined;
		this._credentialBootstrapFiles = getMachineCredentialBootstrapFiles(deps.getAgentDir());
		this._credentialDiscoveryRoots = getMachineCredentialDiscoveryRoots(deps.getAgentDir());
		this._credentialManager = new CredentialManager({
			storage: new BitwardenCredentialStorageRouter({
				secretsManagerCoordinationFile: bitwardenSecretsManagerCoordinationFile(deps.getAgentDir()),
			}),
			resolveProject: resolveCredentialProject,
			resolveMachineSession: (signal) => {
				if (signal?.aborted) return Promise.resolve(undefined);
				return discoverMachineCredentialSession({ candidateFiles: this._credentialBootstrapFiles, signal });
			},
			onEnvironmentChanged: () => {
				const sessionKey = deps.getShellSessionKey();
				disposeShellExecutionSession(sessionKey);
			},
		});
		this._credentialExposureBoundary = {
			redactSensitiveText: (text) => this._credentialManager.redactSensitiveText(text),
			// Data from the retired local vault remains protected even though it is no longer executable.
			protectedFiles: [secretVaultFile(deps.getAgentDir()), ...this._credentialBootstrapFiles],
			// Worker process tools remain host-wide for ordinary sibling-project work, but the
			// recognizable private harness roots still pass through the same execution guard as
			// direct filesystem tools. This is lexical/recognizable protection only; obfuscated
			// runtime path construction requires OS-level isolation.
			protectedDirectories: [
				managedSecretEnvDir(deps.getAgentDir()),
				...(this._workerSessionPrivatePathEnvelope?.deniedPaths ?? []),
			],
		};
		this._fileMutationIntents = new FileMutationIntentController();
	}

	/**
	 * Resolve the construction and activation policy once for a runtime generation. This is the
	 * UAC choke point: callers must consult it before invoking a tool factory, not merely before
	 * placing an already-created definition in the live registry.
	 */
	private _createToolAccessPolicy(): RuntimeToolAccessPolicy {
		const role = getSessionRole();
		const configuredAllowedToolNames = this.deps.getAllowedToolNames();
		const allowedToolNames = configuredAllowedToolNames
			? new Set(mapToolNamesForPlatform([...configuredAllowedToolNames]))
			: undefined;
		const configuredExcludedToolNames = this.deps.getExcludedToolNames();
		const excludedToolNames = configuredExcludedToolNames
			? new Set(mapToolNamesForPlatform([...configuredExcludedToolNames]))
			: undefined;
		const configuredToolProfileFilter = this.deps.getToolProfileFilter();
		const toolProfileFilter = configuredToolProfileFilter
			? {
					allow: mapToolNamesForPlatform(configuredToolProfileFilter.allow),
					block: mapToolNamesForPlatform(configuredToolProfileFilter.block),
				}
			: undefined;
		return {
			allowedToolNames,
			toolProfileFilter,
			allows: (name) => {
				// Strict worker UAC ceiling wins over every explicit grant.
				if (role === "worker" && WORKER_FORBIDDEN_TOOLS.has(name)) return false;
				if (allowedToolNames && !allowedToolNames.has(name)) return false;
				if (excludedToolNames?.has(name)) return false;
				if (!toolProfileFilter) return true;
				if (toolProfileFilter.allow.length > 0 && !matchesResourceProfilePattern(name, toolProfileFilter.allow)) {
					return false;
				}
				if (matchesResourceProfilePattern(name, toolProfileFilter.block)) return false;
				return true;
			},
		};
	}

	private async _runContextScout(
		query: string,
		maxTurns: number | undefined,
		artifactStore: ArtifactStore | undefined,
	) {
		const cwd = this.deps.getCwd();
		const envelope = this.deps.getCapabilityEnvelope?.();
		const childEnvelope = deriveCompositeChildEnvelope("context_scout", ["read", "grep", "find"], envelope);
		const controller = new ScoutController({
			resolveScoutModel: async () =>
				resolveScoutModel(
					this.deps.getModelRegistry(),
					this.deps.getSettingsManager().getScoutSettings().model,
					this.deps.getAgentDir(),
					(model) => this.deps.isModelExhausted(model),
					resolveCurrentToolRepairSettings(this.deps.getSettingsManager().settings).textProtocol,
				),
			getCwd: () => cwd,
			buildReadOnlyTools: (toolCwd) => {
				const readTool = wrapToolWithCapabilityEnvelopeGate(createReadTool(toolCwd), toolCwd, childEnvelope);
				const grepTool = wrapToolWithCapabilityEnvelopeGate(
					createGrepTool(toolCwd, { artifactStore }),
					toolCwd,
					childEnvelope,
				);
				const findTool = wrapToolWithCapabilityEnvelopeGate(
					createFindTool(toolCwd, { artifactStore }),
					toolCwd,
					childEnvelope,
				);
				return [
					wrapToolWithCredentialExposureGuard(readTool, toolCwd, this._credentialExposureBoundary),
					wrapToolWithCredentialExposureGuard(grepTool, toolCwd, this._credentialExposureBoundary),
					wrapToolWithCredentialExposureGuard(findTool, toolCwd, this._credentialExposureBoundary),
				];
			},
			streamFn: this.deps.getAgent().streamFn,
			fileExists: (path) => {
				const target = resolveCwdPath(cwd, path);
				if (childEnvelope && !isPathWithinEnvelope(childEnvelope, target, cwd)) {
					return false;
				}
				return existsSync(target);
			},
			countLines: (path) => {
				const target = resolveCwdPath(cwd, path);
				if (childEnvelope && !isPathWithinEnvelope(childEnvelope, target, cwd)) {
					return undefined;
				}
				return countFileLines(target);
			},
			// Lets the scout register itself in the reload-gate quiesce registry (see
			// reload-blockers.ts) for its own agentDir — the same key `_assertReloadQuiescent` reads.
			getAgentDir: () => this.deps.getAgentDir(),
		});
		return controller.run(query, maxTurns);
	}

	/** Whether a tool name is present in the live wrapped registry. */
	hasTool(name: string): boolean {
		return this._toolRegistry.has(name);
	}

	/** The live wrapped tool for a name, if registered (activation lookup). */
	getRegisteredTool(name: string): AgentTool | undefined {
		return this._toolRegistry.get(name);
	}

	/** A registered tool's normalized prompt snippet, if any. */
	getToolPromptSnippet(name: string): string | undefined {
		return this._toolPromptSnippets.get(name);
	}

	/** A registered tool's normalized prompt guidelines, if any. */
	getToolPromptGuidelines(name: string): string[] | undefined {
		return this._toolPromptGuidelines.get(name);
	}

	/**
	 * Get all configured tools with name, description, parameter schema, prompt guidelines, and source metadata.
	 */
	getAllTools(): ToolInfo[] {
		return Array.from(this._toolDefinitions.values()).map(({ definition, sourceInfo }) => ({
			name: definition.name,
			description: definition.description,
			parameters: definition.parameters,
			promptGuidelines: definition.promptGuidelines,
			sourceInfo,
		}));
	}

	getToolDefinition(name: string): ToolDefinition | undefined {
		return this._toolDefinitions.get(name)?.definition;
	}

	get credentialManager(): CredentialManager {
		return this._credentialManager;
	}

	/** Release session-owned mutation payload leases retained by the write/edit tool pair. */
	dispose(): Promise<void> {
		this._credentialManager.lock();
		return this._fileMutationIntents.dispose();
	}

	refreshToolRegistry(options?: { activeToolNames?: string[]; includeAllExtensionTools?: boolean }): void {
		const previousRegistryNames = new Set(this._toolRegistry.keys());
		// Re-derive from the pre-filter REQUEST, never from agent.state.tools: the active set is
		// capability/profile-filtered, so feeding it back through setActiveToolsByName would
		// permanently shrink what a later switch to a larger model (or permissive profile) restores.
		const previousActiveToolNames = mapToolNamesForPlatform(
			this.deps.getRequestedActiveToolNames() ?? this.deps.getActiveToolNames(),
		);
		const toolAccess = this._createToolAccessPolicy();
		const { allowedToolNames, toolProfileFilter } = toolAccess;
		const isAllowedTool = toolAccess.allows;

		const registeredTools = this.deps.getExtensionRunner().getAllRegisteredTools();
		const allCustomTools = [
			...registeredTools,
			...this.deps.getCustomTools().map((definition) => ({
				definition,
				sourceInfo: createSyntheticSourceInfo(`<sdk:${definition.name}>`, { source: "sdk" }),
			})),
			// Memory subsystem provider tools (e.g. file-store's `memory` tool).
			...this.deps
				.getMemoryManager()
				.getToolDefinitions()
				.map((definition) => ({
					definition,
					sourceInfo: createSyntheticSourceInfo(`<memory:${definition.name}>`, { source: "sdk" }),
				})),
		].filter((tool) => isAllowedTool(tool.definition.name));
		const definitionRegistry = new Map<string, ToolDefinitionEntry>(
			Array.from(this._baseToolDefinitions.entries())
				.filter(([name]) => isAllowedTool(name))
				.map(([name, definition]) => [
					name,
					{
						definition,
						sourceInfo: createSyntheticSourceInfo(`<builtin:${name}>`, { source: "builtin" }),
					},
				]),
		);
		for (const tool of allCustomTools) {
			definitionRegistry.set(tool.definition.name, {
				definition: tool.definition,
				sourceInfo: tool.sourceInfo,
			});
		}
		this._toolDefinitions = definitionRegistry;
		this._toolPromptSnippets = new Map(
			Array.from(definitionRegistry.values())
				.map(({ definition }) => {
					const snippet = this.deps.normalizePromptSnippet(definition.promptSnippet);
					return snippet ? ([definition.name, snippet] as const) : undefined;
				})
				.filter((entry): entry is readonly [string, string] => entry !== undefined),
		);
		this._toolPromptGuidelines = new Map(
			Array.from(definitionRegistry.values())
				.map(({ definition }) => {
					const guidelines = this.deps.normalizePromptGuidelines(definition.promptGuidelines);
					return guidelines.length > 0 ? ([definition.name, guidelines] as const) : undefined;
				})
				.filter((entry): entry is readonly [string, string[]] => entry !== undefined),
		);
		const runner = this.deps.getExtensionRunner();
		const wrappedExtensionTools = wrapRegisteredTools(allCustomTools, runner);
		const wrappedBuiltInTools = wrapRegisteredTools(
			Array.from(this._baseToolDefinitions.values())
				.filter((definition) => isAllowedTool(definition.name))
				.map((definition) => ({
					definition,
					sourceInfo: createSyntheticSourceInfo(`<builtin:${definition.name}>`, { source: "builtin" }),
				})),
			runner,
		);

		const toolRegistry = new Map(
			wrappedBuiltInTools.map((tool) => {
				const guarded = wrapToolWithCredentialExposureGuard(
					tool,
					this.deps.getCwd(),
					this._credentialExposureBoundary,
				);
				const scoped = this._workerSessionPrivatePathEnvelope
					? wrapToolWithEnvelopeScope(guarded, this._workerSessionPrivatePathEnvelope, this.deps.getCwd())
					: guarded;
				return [scoped.name, scoped] as const;
			}),
		);
		for (const tool of wrappedExtensionTools as AgentTool[]) {
			const guarded = wrapToolWithCredentialExposureGuard(
				tool,
				this.deps.getCwd(),
				this._credentialExposureBoundary,
			);
			const scoped = this._workerSessionPrivatePathEnvelope
				? wrapToolWithEnvelopeScope(guarded, this._workerSessionPrivatePathEnvelope, this.deps.getCwd())
				: guarded;
			toolRegistry.set(scoped.name, scoped);
		}
		this._toolRegistry = toolRegistry;

		const requestedBase = options?.activeToolNames
			? mapToolNamesForPlatform(options.activeToolNames)
			: [...previousActiveToolNames];
		const nextActiveToolNames = requestedBase.filter((name) => isAllowedTool(name));

		const persistentAutoActivated: string[] = [];
		if (allowedToolNames) {
			for (const toolName of this._toolRegistry.keys()) {
				if (allowedToolNames.has(toolName)) {
					nextActiveToolNames.push(toolName);
					persistentAutoActivated.push(toolName);
				}
			}
		} else if (options?.includeAllExtensionTools) {
			for (const tool of wrappedExtensionTools) {
				nextActiveToolNames.push(tool.name);
				persistentAutoActivated.push(tool.name);
			}
		} else if (!options?.activeToolNames) {
			for (const toolName of this._toolRegistry.keys()) {
				if (!previousRegistryNames.has(toolName)) {
					nextActiveToolNames.push(toolName);
					persistentAutoActivated.push(toolName);
				}
			}
		}

		// Strict UAC: the active profile is the COMPLETE grant, so a tool the profile names
		// explicitly is itself a request for that tool — it must ACTIVATE from the registry even
		// if the session never requested it. Without this, activation is only ever the requested
		// defaults ∩ allow-list, and a profile granting non-default tools (a search-only profile's
		// grep/find) yields an empty or truncated tool set on load and /reload. A blanket "*"
		// stays grant-only: activation then still derives from the request/defaults above.
		const explicitAllowPatterns = toolProfileFilter?.allow.filter((pattern) => pattern !== "*") ?? [];
		if (explicitAllowPatterns.length > 0) {
			const boundPatterns = new Set<string>();
			for (const toolName of this._toolRegistry.keys()) {
				if (!isAllowedTool(toolName)) continue;
				for (const pattern of explicitAllowPatterns) {
					if (matchesResourceProfilePattern(toolName, [pattern])) boundPatterns.add(pattern);
				}
				if (matchesResourceProfilePattern(toolName, explicitAllowPatterns)) {
					nextActiveToolNames.push(toolName);
				}
			}
			// An explicit grant that binds to NO registered tool is a silent no-op — typo'd
			// name, or the owning extension is not granted/loaded. Surface it.
			this.deps.setUnboundToolGrantWarnings(
				explicitAllowPatterns
					.filter((pattern) => !boundPatterns.has(pattern))
					.map(
						(pattern) =>
							`profile tool grant "${pattern}" binds to no registered tool (typo, or the owning extension is not granted/loaded)`,
					),
			);
		} else {
			this.deps.setUnboundToolGrantWarnings([]);
		}

		// artifact_retrieve companion auto-activation is enforced inside
		// setActiveToolsByName() itself (not duplicated here), so every activation path --
		// including the public, extension-exposed setActiveTools() -- gets the same
		// guarantee, not just this settings/profile refresh flow.
		this.deps.setActiveToolsByName([...new Set(nextActiveToolNames)]);
		// setActiveToolsByName just stored the profile-filtered ACTIVE set as the request; restore
		// the true pre-filter request (plus non-profile auto-activations) so an internal refresh can
		// never permanently narrow it. Explicit profile grants are generation-local activations: if
		// profile A names grep and profile B later grants "*", A must not pin grep into B's request.
		this.deps.setRequestedActiveToolNames([...new Set([...requestedBase, ...persistentAutoActivated])]);
	}

	private _createReloadRuntimeSnapshot(): ReloadRuntimeSnapshot {
		const agent = this.deps.getAgent();
		const toolProfileFilter = this.deps.getToolProfileFilter();
		const requestedActiveToolNames = this.deps.getRequestedActiveToolNames();
		return {
			extensionRunner: this.deps.getExtensionRunner(),
			settings: this.deps.getSettingsManager().createReloadSnapshot(),
			modelRegistry: this.deps.getModelRegistry().createReloadSnapshot(),
			model: agent.state.model,
			thinkingLevel: agent.state.thinkingLevel,
			sessionLeafId: this.deps.getSessionManager().getLeafId(),
			toolProfileFilter: toolProfileFilter
				? { allow: [...toolProfileFilter.allow], block: [...toolProfileFilter.block] }
				: undefined,
			requestedActiveToolNames: requestedActiveToolNames ? [...requestedActiveToolNames] : undefined,
			memory: this.deps.createMemoryReloadSnapshot(),
			profileFilter: this.deps.createProfileFilterReloadSnapshot(),
			unboundToolGrantWarnings: [...this.deps.getUnboundToolGrantWarnings()],
			baseToolDefinitions: this._baseToolDefinitions,
			toolRegistry: this._toolRegistry,
			toolDefinitions: this._toolDefinitions,
			toolPromptSnippets: this._toolPromptSnippets,
			toolPromptGuidelines: this._toolPromptGuidelines,
			agentTools: agent.state.tools,
			agentSystemPrompt: agent.state.systemPrompt,
			baseSystemPrompt: this.deps.getBaseSystemPrompt(),
		};
	}

	private _restoreReloadRuntimeSnapshot(snapshot: ReloadRuntimeSnapshot): void {
		this.deps.getSettingsManager().restoreReloadSnapshot(snapshot.settings);
		this.deps.getModelRegistry().restoreReloadSnapshot(snapshot.modelRegistry);
		this.deps.setToolProfileFilter(
			snapshot.toolProfileFilter
				? { allow: [...snapshot.toolProfileFilter.allow], block: [...snapshot.toolProfileFilter.block] }
				: undefined,
		);
		this.deps.setRequestedActiveToolNames(
			snapshot.requestedActiveToolNames ? [...snapshot.requestedActiveToolNames] : undefined,
		);
		this.deps.restoreMemoryReloadSnapshot(snapshot.memory);
		this.deps.restoreProfileFilterReloadSnapshot(snapshot.profileFilter);
		this.deps.setUnboundToolGrantWarnings([...snapshot.unboundToolGrantWarnings]);
		// setExtensionRunner restores both _extensionRunner and _extensionRunnerRef.current together
		// (the same field-then-ref pair the host wrote at every assignment site); nothing reads the
		// ref between the two writes, so folding them is unobservable.
		this.deps.setExtensionRunner(snapshot.extensionRunner);
		this._baseToolDefinitions = snapshot.baseToolDefinitions;
		this._toolRegistry = snapshot.toolRegistry;
		this._toolDefinitions = snapshot.toolDefinitions;
		this._toolPromptSnippets = snapshot.toolPromptSnippets;
		this._toolPromptGuidelines = snapshot.toolPromptGuidelines;
		const agent = this.deps.getAgent();
		agent.state.model = snapshot.model;
		agent.state.thinkingLevel = snapshot.thinkingLevel;
		agent.state.tools = snapshot.agentTools;
		agent.state.systemPrompt = snapshot.agentSystemPrompt;
		this.deps.setBaseSystemPrompt(snapshot.baseSystemPrompt);
		const sessionManager = this.deps.getSessionManager();
		if (snapshot.sessionLeafId === null) {
			sessionManager.resetLeaf();
		} else {
			sessionManager.branch(snapshot.sessionLeafId);
		}
		this.deps.applyExtensionBindings(snapshot.extensionRunner);
	}

	private _doctorReloadRuntime(): void {
		const extensionErrors = this.deps.getResourceLoader().getExtensions().errors;
		if (extensionErrors.length > 0) {
			const summary = extensionErrors
				.slice(0, 6)
				.map((error) => `${error.path}: ${error.error}`)
				.join("; ");
			throw new Error(`Extension reload failed doctor: ${summary}`);
		}

		const missingActiveTools = this.deps.getActiveToolNames().filter((name) => !this._toolRegistry.has(name));
		if (missingActiveTools.length > 0) {
			throw new Error(
				`Extension reload failed doctor: active tool(s) missing after reload: ${missingActiveTools.join(", ")}`,
			);
		}

		for (const tool of this.deps.getAgent().state.tools) {
			if (!this._toolDefinitions.has(tool.name)) {
				throw new Error(`Extension reload failed doctor: tool ${tool.name} missing from definition registry`);
			}
		}

		this.deps.createAgentContextSnapshot();
		this.deps.getContextUsage();
	}

	buildRuntime(options: {
		activeToolNames?: string[];
		flagValues?: Map<string, boolean | string>;
		includeAllExtensionTools?: boolean;
		onError?: ExtensionErrorListener;
	}): (() => void) | undefined {
		const settingsManager = this.deps.getSettingsManager();
		const autoResizeImages = settingsManager.getImageAutoResize();
		const shellCommandPrefix = settingsManager.getShellCommandPrefix();
		const shellPath = settingsManager.getShellPath();
		const baseToolsOverride = this.deps.getBaseToolsOverride();
		const toolAccess = this._createToolAccessPolicy();
		// Artifact-producing tools must not emit a "Full output: artifact tool-output:<id>" handle
		// that nothing can resolve. If artifact_retrieve is explicitly excluded/blocked/outside
		// an active allowlist, don't hand grep/find/run_toolkit_script an artifact store at all:
		// they fall back to their bounded preview/truncation behavior, with no payload/meta files
		// ever written and no retrieval promise made.
		const toolArtifactStore =
			!baseToolsOverride && toolAccess.allows("artifact_retrieve") ? this.deps.getToolArtifactStore() : undefined;
		const resourceLoader = this.deps.getResourceLoader();
		const toolOptions: ToolDefinitionOptions = {
			read: { autoResizeImages },
			bash: {
				commandPrefix: shellCommandPrefix,
				shellPath,
				sessionKey: this.deps.getShellSessionKey(),
				platform: process.platform,
				spawnHook: (context) => {
					const env = { ...context.env, ...this._credentialManager.getEnvironmentForCwd(context.cwd) };
					delete env.BW_SESSION;
					return { ...context, env };
				},
			},
			python: {
				environment: (cwd) => this._credentialManager.getEnvironmentForCwd(cwd) ?? {},
				omitEnvironmentVariables: ["BW_SESSION"],
			},
			write: { intentController: this._fileMutationIntents },
			edit: { intentController: this._fileMutationIntents },
			grep: { artifactStore: toolArtifactStore },
			find: { artifactStore: toolArtifactStore },
			artifact_retrieve: { artifactStore: toolArtifactStore },
			extensionify: {
				agentDir: this.deps.getAgentDir(),
				loadExtension: async ({ extensionPath, cwd }) => {
					if (!resourceLoader.loadIsolatedExtension) {
						return { extension: null, error: "Isolated extension loading is unavailable in this runtime" };
					}
					return resourceLoader.loadIsolatedExtension(extensionPath, cwd);
				},
			},
		};
		this._baseToolDefinitions = baseToolsOverride
			? new Map(
					Object.entries(baseToolsOverride)
						.filter(([name]) => toolAccess.allows(name))
						.map(([name, tool]) => [name, createToolDefinitionFromAgentTool(tool)]),
				)
			: new Map(
					[...allToolNames]
						.filter((name) => toolAccess.allows(name))
						.map((name) => [name, createToolDefinitionWithRuntime(name, this.deps.getCwd(), toolOptions)]),
				);
		const toolTaskDependencies = this.deps.getToolTaskDependencies?.();
		if (toolTaskDependencies && toolAccess.allows("tool_task")) {
			this._baseToolDefinitions.set("tool_task", createToolTaskToolDefinition(toolTaskDependencies));
		}
		if (!baseToolsOverride) {
			if (toolAccess.allows("skill")) {
				const definition = createSkillVaultToolDefinition(this.deps.getSkillVault());
				this._baseToolDefinitions.set(definition.name, definition);
			}
			if (toolAccess.allows("run_process")) {
				const orchestrationProfile = this.deps.getOrchestrationProfile?.();
				if (orchestrationProfile?.executionPolicy) {
					this._baseToolDefinitions.set(
						"run_process",
						createRunProcessToolDefinition(this.deps.getCwd(), {
							policy: orchestrationProfile.executionPolicy,
							maxWallClockMs: orchestrationProfile.budget.maxWallClockMs ?? 0,
							environment: (cwd) => this._credentialManager.getEnvironmentForCwd(cwd) ?? {},
						}),
					);
				}
			}
			if (toolAccess.allows("context_audit")) {
				for (const definition of createCoreDiagnosticsToolDefinitions(
					() => this.deps.getActiveToolNames(),
					() => this.getAllTools(),
					(messages) => this.deps.getContextGcReport(messages),
					() => this.deps.getMemoryAuditDiagnostics(),
				)) {
					this._baseToolDefinitions.set(definition.name, definition);
				}
			}
			// Hoisted above the goal-tool registration (rather than declared only inside the
			// worktreeSync-enabled block further below) so the SAME settings/deps factory backs both
			// the goal->tmux lane-first dispatch dep (`createLaneWorktree`, immediately below) and the
			// `worktree_sync` tool registration later in this method -- one source of truth, no drift.
			const worktreeSyncSettings =
				toolAccess.allows(LEGACY_GOAL_TOOL_NAME) || toolAccess.allows("worktree_sync")
					? settingsManager.getWorktreeSyncSettings()
					: undefined;
			const worktreeSyncEngineDeps = () =>
				buildWorktreeSyncEngineDeps({
					cwd: this.deps.getCwd(),
					agentDir: this.deps.getAgentDir(),
					settingsManager: this.deps.getSettingsManager(),
					sessionId: this.deps.getSessionManager().getSessionId(),
				});
			const shouldBuildGoalExecutor =
				toolAccess.allows(LEGACY_GOAL_TOOL_NAME) || GOAL_LIFECYCLE_TOOL_NAMES.some(toolAccess.allows);
			if (shouldBuildGoalExecutor) {
				const goalToolDefinition = createGoalToolDefinition({
					getGoalState: () => this.deps.getGoalStateSnapshot(),
					authorizeStart: (input) =>
						this.deps.authorizeGoalStartFromTool ? this.deps.authorizeGoalStartFromTool(input) : null,
					saveGoalState: (state, expected) => {
						this.deps.saveGoalStateSnapshot(state, expected);
					},
					// kind:"tool" evidence refs verify against real session records.
					hasToolCallId: (toolCallId) => hasAnsweredToolCallOnBranch(this.deps.getSessionManager(), toolCallId),
					resolveToolEvidence: (uri) => {
						const completed = isCompletedBackgroundToolEvidence(
							this.deps.getToolTaskDependencies?.().list() ?? [],
							uri,
						);
						if (completed !== undefined) return completed;
						return hasAnsweredToolCallOnBranch(this.deps.getSessionManager(), uri);
					},
					// kind:"worker" evidence refs verify against the SAME live lane/claim accessors the
					// delegate action=status uses below -- live wiring (these were declared optional
					// and read-defensive on the goal-tool deps type).
					getLaneRecords: () => this.deps.getWorkerLaneRecords(),
					getWorkerClaimSnapshots: () => this.deps.getWorkerClaimSnapshots(),
					// dispatch_worker's tool-layer side effect for the default (in_process) route: starts a
					// real in-process worker lane through the SAME starter the delegate tool uses (below),
					// adapted from its `{started:true;record}|{started:false;skipReason}` shape onto this
					// tool's narrower `{laneId}|{skipReason}` shape.
					startWorkerDelegation: (args) => {
						const outcome = this.deps.startWorkerDelegation(createGoalWorkerDelegationRequest(args));
						return outcome.started ? { laneId: outcome.record.laneId } : { skipReason: outcome.skipReason };
					},
					// dispatch_worker's tool-layer side effect for `dispatchTarget:"tmux"`: core structurally
					// invokes the SAME tmux_agent_manager fire_task call the model itself would make (no
					// extension change, no faked launch/laneId) -- see tmux-dispatch.ts's doc comment.
					dispatchTmuxWorker: (args) =>
						dispatchTmuxWorker(
							{
								getToolDefinition: (name) => this.getToolDefinition(name),
								createExtensionContext: () => this.deps.getExtensionRunner().createContext(),
								resolveManagedLaneId: (id) => this.deps.resolveManagedLaneId(id),
								getGoalId: () => this.deps.getGoalStateSnapshot()?.goalId,
								evaluateWorkerLaneRefusal: () => this.deps.getLaneWorkerRefusal(),
								// Lane-first dispatch, wired ONLY when worktree-sync is enabled -- absent otherwise,
								// so a disabled/default session gets the existing byte-identical fire_task params.
								createLaneWorktree: worktreeSyncSettings?.enabled
									? async (laneArgs) => {
											const created = await createLane(worktreeSyncEngineDeps(), {
												...(laneArgs.goalId !== undefined ? { goalId: laneArgs.goalId } : {}),
												requirementId: laneArgs.requirementId,
											});
											// Never fake a lane: any non-"ok" engine code (max lanes, invalid key, git
											// error, ...) is refused, mapped by the caller onto the stable
											// "worktree_create_failed" skip reason before any fire_task call runs.
											if (created.code !== "ok") return { skipReason: created.code };
											return { laneKey: created.lane.laneKey, worktreePath: created.lane.worktreePath };
										}
									: undefined,
							},
							args,
						),
					cwd: () => this.deps.getCwd(),
					// Reuses the already branch-scoped getTaskStepsStateSnapshot dep -- no new
					// SessionManager access needed for the cross-visibility nudge.
					getOpenTaskSteps: () => deriveOpenTaskStepRefs(this.deps.getTaskStepsStateSnapshot()),
					getBackgroundToolTasks: () => this.deps.getToolTaskDependencies?.().list() ?? [],
					getActivePipeline: () => {
						const run = resolveCurrentProjectPipelineRun(this.deps.getCwd(), this.deps.getPipelineRunSnapshot());
						if (!run || !isPipelineRunActive(run)) return undefined;
						return {
							runId: run.runId,
							pipelineName: run.pipelineName,
							goalId: run.goalId,
							status: run.status,
						};
					},
				});
				if (toolAccess.allows(LEGACY_GOAL_TOOL_NAME)) {
					this._baseToolDefinitions.set(goalToolDefinition.name, goalToolDefinition);
				}
				for (const definition of createGoalLifecycleToolDefinitions(goalToolDefinition)) {
					if (toolAccess.allows(definition.name)) {
						this._baseToolDefinitions.set(definition.name, definition);
					}
				}
			}
			if (toolAccess.allows("task_steps")) {
				const taskStepsToolDefinition = createTaskStepsToolDefinition({
					getTaskStepsState: () => this.deps.getTaskStepsStateSnapshot(),
					saveTaskStepsState: (state) => {
						this.deps.saveTaskStepsStateSnapshot(state);
					},
					getActivePipelineScope: () => {
						const run = resolveCurrentProjectPipelineRun(this.deps.getCwd(), this.deps.getPipelineRunSnapshot());
						if (!run || !isPipelineRunActive(run)) return undefined;
						const definition = resolvePipelineDefinitionForRun(
							{
								agentPipelinesDir: resourceDir("pipelines", this.deps.getAgentDir()),
								cwd: this.deps.getCwd(),
							},
							run,
						);
						return { runId: run.runId, stageIds: definition?.stages.map((stage) => stage.id) ?? [] };
					},
				});
				this._baseToolDefinitions.set(taskStepsToolDefinition.name, taskStepsToolDefinition);
			}
			if (toolAccess.allows("pipeline")) {
				const pipelineToolDefinition = createPipelineToolDefinition({
					cwd: () => this.deps.getCwd(),
					agentPipelinesDir: () => resourceDir("pipelines", this.deps.getAgentDir()),
					getPipelineRun: () => this.deps.getPipelineRunSnapshot(),
					savePipelineRun: (run) => {
						this.deps.savePipelineRunSnapshot(run);
					},
					getGoalState: () => this.deps.getGoalStateSnapshot(),
					getOpenTaskSteps: () => deriveOpenTaskStepRefs(this.deps.getTaskStepsStateSnapshot()),
					getBackgroundToolTasks: () => this.deps.getToolTaskDependencies?.().list() ?? [],
				});
				this._baseToolDefinitions.set(pipelineToolDefinition.name, pipelineToolDefinition);
			}
			if (toolAccess.allows("ask_question")) {
				const askQuestionToolDefinition = createAskQuestionToolDefinition({
					sessionManager: this.deps.getSessionManager(),
					artifactStore: toolArtifactStore,
					getImageStore: () => this.deps.getSessionImageStore(),
				});
				this._baseToolDefinitions.set(askQuestionToolDefinition.name, askQuestionToolDefinition);
			}
			if (toolAccess.allows("secret_store")) {
				const secretStoreToolDefinition = createSecretStoreToolDefinition({
					manager: this._credentialManager,
					discoverMigrationSources: (cwd, signal) =>
						discoverCredentialMigrationSources(cwd, signal, process.env, {
							additionalRoots: this._credentialDiscoveryRoots,
							excludedFiles: this._credentialBootstrapFiles,
						}),
				});
				this._baseToolDefinitions.set(secretStoreToolDefinition.name, secretStoreToolDefinition);
			}
			if (toolAccess.allows("delegate")) {
				const workerAgentControl = this.deps.workerAgentControl;
				const profileWriter =
					workerAgentControl?.inspectTaskProfileOptions && workerAgentControl.createTaskProfile
						? {
								inspectTaskProfileOptions: () => workerAgentControl.inspectTaskProfileOptions!(),
								createTaskProfile: (input: Parameters<TaskProfileWriterPort["createTaskProfile"]>[0]) =>
									workerAgentControl.createTaskProfile!(input),
							}
						: undefined;
				// Collected synchronously: normalizeProviderPromptGuidelines's onBounded callback fires
				// only while createDelegateToolDefinition below computes promptGuidelines, so the whole
				// batch is ready to hand to the /context sink the moment construction returns.
				const delegatePromptGuidelineWarnings: string[] = [];
				const delegateToolDefinition = createDelegateToolDefinition({
					caller: { kind: "session_root" },
					resolveMessageReplayScope: () => {
						const sessionManager = this.deps.getSessionManager();
						return {
							sessionId: sessionManager.getSessionId(),
							branchId: sessionManager.getLeafId() ?? sessionManager.getSessionId(),
						};
					},
					startWorkerDelegation: (args) => this.deps.startWorkerDelegation(args),
					runWorkerDelegation: (args) => this.deps.runWorkerDelegationOnce(args),
					orchestrationProfiles: this.deps.getOrchestrationProfileCatalog(),
					workerModelPinPolicy: settingsManager.getWorkerModelPinPolicy(),
					...(workerAgentControl ? { workerAgentControl } : {}),
					status: {
						getLaneRecords: () => this.deps.getWorkerLaneRecords(),
						getWorkerClaimSnapshots: () => this.deps.getWorkerClaimSnapshots(),
						...(this.deps.getWorkerResult
							? { getWorkerResult: (laneId: string) => this.deps.getWorkerResult?.(laneId) }
							: {}),
						acknowledgeWorkerReview: (requestId) =>
							acknowledgeWorkerClaimReview(this.deps.getSessionManager(), requestId),
					},
					...(profileWriter ? { profileWriter } : {}),
					warn: (message) => delegatePromptGuidelineWarnings.push(message),
				});
				this._baseToolDefinitions.set(delegateToolDefinition.name, delegateToolDefinition);
				this.deps.setDelegatePromptGuidelineWarnings?.(delegatePromptGuidelineWarnings);
			}
			// Registered but not default-active: probes spend tokens on the probed model, so
			// activation is an explicit choice (settings/profile/setActiveTools or /autonomy fitness).
			if (toolAccess.allows("model_fitness")) {
				const modelFitnessToolDefinition = createModelFitnessToolDefinition({
					runProbe: (args) => this.deps.runModelFitness(args),
				});
				this._baseToolDefinitions.set(modelFitnessToolDefinition.name, modelFitnessToolDefinition);
			}
			if (toolAccess.allows("context_scout") && settingsManager.getScoutSettings().enabled) {
				const contextScoutToolDefinition = createContextScoutToolDefinition({
					runScout: (input) => this._runContextScout(input.query, input.maxTurns, toolArtifactStore),
				});
				this._baseToolDefinitions.set(contextScoutToolDefinition.name, contextScoutToolDefinition);
			}
			if (toolAccess.allows("improvement_loop")) {
				const improvementLoopTool: ToolDef = createImprovementLoopTool(async (command, args, options) =>
					execCommand(command, args, options?.cwd ?? this.deps.getCwd(), {
						timeout: options?.timeout,
						signal: options?.signal,
						maxBuffer: options?.maxBuffer,
						env: getShellEnv(),
					}),
				);
				this._baseToolDefinitions.set("improvement_loop", improvementLoopTool);
			}
			if (toolAccess.allows("run_toolkit_script")) {
				const runToolkitScriptToolDefinition = createRunToolkitScriptToolDefinition({
					getScripts: () => this.deps.getSettingsManager().getToolkitScripts(),
					execute: (script, scriptArgs) => executeToolkitScript({ script, scriptArgs, cwd: this.deps.getCwd() }),
					artifactStore: toolArtifactStore,
					// Reflex brain (fitness-gated local model): resolves ambiguous requests into a
					// registry pick. Best-effort — absent/unfit brain keeps the shortlist behavior.
					interpret: async (request, scripts) => {
						const model = this.deps.resolveCurationModelIfFit();
						if (!model) return undefined;
						return runReflexInterpreterCompletion({
							request,
							scripts,
							model,
							laneKind: "toolkit-brain",
							usageKind: "toolkit-brain",
							usageLabel: "toolkit-brain",
							sessionId: this.deps.getSessionManager().getSessionId(),
							completionRunner: this.deps,
							usageReporter: this.deps,
						});
					},
				});
				this._baseToolDefinitions.set(runToolkitScriptToolDefinition.name, runToolkitScriptToolDefinition);
			}

			// Worktree-sync (opt-in): the closed-action lane workflow tool, plus -- for a session
			// launched lane-bound (PI_WORKTREE_LANE) -- the G8/G10 lane gate wrapped UNDER the
			// file-mutation tools (edit/write, plus integration-sensitive bash checks), so a sync_required lane fails closed with the
			// exact recovery step rather than relying on prompt compliance. `worktreeSyncSettings` /
			// `worktreeSyncEngineDeps` are hoisted above (declared before the goal-tool registration)
			// so the goal->tmux lane-first dispatch dep and this tool share one settings/deps source.
			if (toolAccess.allows("worktree_sync") && worktreeSyncSettings?.enabled) {
				const worktreeSyncToolDefinition = createWorktreeSyncToolDefinition({
					engineDeps: worktreeSyncEngineDeps,
					settings: () => this.deps.getSettingsManager().getWorktreeSyncSettings(),
					boundLaneKey: () => getBoundWorktreeLaneKey(),
					isWorker: () => isWorkerSession(),
				});
				this._baseToolDefinitions.set(worktreeSyncToolDefinition.name, worktreeSyncToolDefinition);

				const boundLaneKey = getBoundWorktreeLaneKey();
				if (boundLaneKey) {
					const laneGate = new WorktreeLaneGate({
						laneKey: boundLaneKey,
						engineDeps: worktreeSyncEngineDeps,
						policy: () => this.deps.getSettingsManager().getWorktreeSyncSettings().syncPolicy,
					});
					for (const gatedToolName of ["edit", "write", "bash"]) {
						const original = this._baseToolDefinitions.get(gatedToolName);
						if (!original) continue;
						this._baseToolDefinitions.set(gatedToolName, {
							...original,
							execute: async (toolCallId, params, signal, onUpdate, ctx) => {
								const bashCommand =
									gatedToolName === "bash" ? (params as { command?: string } | undefined)?.command : undefined;
								const rawPath =
									gatedToolName === "edit" || gatedToolName === "write"
										? (params as { path?: string } | undefined)?.path
										: undefined;
								const targetPath =
									rawPath !== undefined ? resolveToCwd(rawPath, this.deps.getCwd()) : undefined;
								const check = await laneGate.checkMutation(gatedToolName, bashCommand, targetPath);
								if (!check.allowed) {
									return {
										content: [{ type: "text" as const, text: check.message }],
										details: { code: check.code } as never,
									};
								}
								return original.execute(toolCallId, params, signal, onUpdate, ctx);
							},
						});
					}
				}
			}
		}

		const extensionsResult = this.deps.getResourceLoader().getExtensions();
		if (options.flagValues) {
			for (const [name, value] of options.flagValues) {
				extensionsResult.runtime.flagValues.set(name, value);
			}
		}
		const extensions = this.deps.filterExtensionsForRuntime(
			extensionsResult.extensions,
			this._explicitLiveExtensionPaths,
		);
		const runtimeExtensionPaths = new Set(extensions.map((extension) => extension.path));
		extensionsResult.runtime.pendingProviderRegistrations =
			extensionsResult.runtime.pendingProviderRegistrations.filter((registration) =>
				runtimeExtensionPaths.has(registration.extensionPath),
			);

		const runner = new ExtensionRunner(
			extensions,
			extensionsResult.runtime,
			this.deps.getCwd(),
			this.deps.getSessionManager(),
			this.deps.getModelRegistry(),
		);
		const offBuildErrors = options.onError ? runner.onError(options.onError) : undefined;
		this.deps.setExtensionRunner(runner);
		try {
			this.deps.bindExtensionCore(runner);
		} catch (error) {
			offBuildErrors?.();
			throw error;
		}
		this.deps.applyExtensionBindings(runner);

		const boundLaneKey = getBoundWorktreeLaneKey();
		const laneWorker = boundLaneKey !== undefined && isWorkerSession();
		const defaultActiveToolNames = mapToolNamesForPlatform(
			baseToolsOverride
				? Object.keys(baseToolsOverride)
				: [...DEFAULT_ACTIVE_TOOL_NAMES, ...(settingsManager.getScoutSettings().enabled ? ["context_scout"] : [])],
		);
		const baseActiveToolNames = mapToolNamesForPlatform(options.activeToolNames ?? defaultActiveToolNames);
		if (
			laneWorker &&
			settingsManager.getWorktreeSyncSettings().enabled &&
			!baseActiveToolNames.includes("worktree_sync")
		) {
			baseActiveToolNames.push("worktree_sync");
		}
		this.refreshToolRegistry({
			activeToolNames: baseActiveToolNames,
			includeAllExtensionTools: options.includeAllExtensionTools,
		});
		return offBuildErrors;
	}

	reload(): Promise<void> {
		if (this._reloadPromise) {
			// State can change while a generation is being validated. Coalesce any number of
			// overlapping requests into one follow-up generation instead of silently treating a
			// later profile/settings mutation as part of the already-snapshotted generation.
			this._reloadRequested = true;
			return this._reloadPromise;
		}
		const reloadPromise = this._drainReloadRequests();
		this._reloadPromise = reloadPromise;
		return reloadPromise;
	}

	private async _drainReloadRequests(): Promise<void> {
		let finalError: unknown;
		try {
			do {
				this._reloadRequested = false;
				try {
					await this._reloadOnce();
					finalError = undefined;
				} catch (error) {
					finalError = error;
				}
			} while (this._reloadRequested);
			if (finalError !== undefined) throw finalError;
		} finally {
			this._reloadPromise = undefined;
			this._reloadRequested = false;
		}
	}

	/**
	 * Unified reload-gate quiescence check: refuses the caller's action (message-prefixed by
	 * `action`) while the agent is streaming, is compacting, or ANY background work unit is still
	 * registered in the in-process quiesce registry — background lanes (research/worker/
	 * model-fitness), a context-scout run, or an isolated completion (see reload-blockers.ts). Every
	 * live-op entry point below calls this so they refuse identically; this is a synchronous refusal
	 * (not a wait/poll) — callers retry via the same coalescing `reload()` already provides.
	 */
	private _assertReloadQuiescent(action: string): void {
		if (this.deps.isStreaming()) {
			throw new Error(`Cannot ${action} while the agent is streaming or a tool call is active`);
		}
		if (this.deps.isCompacting()) {
			throw new Error(`Cannot ${action} while context compaction or branch summarization is active`);
		}
		const units = getInFlightWorkUnits(this.deps.getAgentDir());
		if (units.length > 0) {
			const summary = units.map(describeInFlightWorkUnit).join(", ");
			throw new Error(`Cannot ${action} while background work is in flight: ${summary}`);
		}
	}

	private async _reloadOnce(): Promise<void> {
		this._assertReloadQuiescent("reload");
		const previousRunner = this.deps.getExtensionRunner();
		const snapshot = this._createReloadRuntimeSnapshot();
		// Preserve the pre-filter tool REQUEST across the rebuild, not the capability/profile-filtered
		// active set — otherwise a reload under a small model permanently shrinks the restorable set.
		const activeToolNames = this.deps.getRequestedActiveToolNames() ?? this.deps.getActiveToolNames();
		const previousFlagValues = previousRunner.getFlagValues();
		const previousExtensionProviderNames = previousRunner.getRegisteredProviderNames();
		const reloadErrors: string[] = [];
		let newRunner: ExtensionRunner | undefined;
		let offReloadErrors: (() => void) | undefined;
		try {
			await this.deps.getSettingsManager().reload();
			// Re-derive the resource-profile tool filter from the freshly reloaded settings.
			// Unlike skills/prompts/themes (which re-filter through the resource loader on every
			// reload), the tool filter is held on the session, so without this a live edit to the
			// active profile's tools allow/block — or switching the active profile — would not
			// apply on /reload and allowed tools would stay missing.
			this.deps.setToolProfileFilter(this.deps.deriveToolProfileFilter());
			// Resource discovery must consume this exact settings generation. A second settings read here
			// could combine profile state from one generation with resources from the next.
			await this.deps.getResourceLoader().reload({
				failOnExtensionErrors: true,
				deferExtensionDispose: true,
				skipSettingsReload: true,
			});
			// A live-load call is an exact session-owned grant. Resource discovery deliberately cannot
			// recreate it without a profile, so restore only those approved paths into the new runtime
			// generation before binding it.
			for (const extensionPath of this._explicitLiveExtensionPaths) {
				if (
					this.deps.isExtensionPathAllowed(extensionPath, "explicit") &&
					!this.deps.getResourceLoader().getLoadedExtension(extensionPath)
				) {
					await this._importExtension(extensionPath);
				}
			}
			// Replace the previous extension-owned provider generation before binding the new one. The
			// bulk refresh also preserves API/OAuth streams for surviving non-extension providers.
			this.deps.getModelRegistry().unregisterProviders(previousExtensionProviderNames);
			offReloadErrors = this.buildRuntime({
				activeToolNames,
				flagValues: previousFlagValues,
				includeAllExtensionTools: true,
				onError: (error) => {
					reloadErrors.push(`${error.extensionPath} ${error.event}: ${error.error}`);
				},
			});
			newRunner = this.deps.getExtensionRunner();
			// Extensions are now bound and their queued providers/models are registered, so a profile may
			// safely select a model contributed by an extension granted in that same profile generation.
			await this.deps.reapplyActiveProfileModelSettings();
			// Model capability and system-prompt/tool exposure must reflect the newly selected model.
			this.refreshToolRegistry({ activeToolNames, includeAllExtensionTools: true });
			try {
				this._doctorReloadRuntime();
				// Reload starts memory providers fresh; loaded extensions re-register below.
				this.deps.clearPendingMemoryProviders();
				const hasBindings =
					this.deps.getExtensionUIContext() ||
					this.deps.getExtensionCommandContextActions() ||
					this.deps.getExtensionShutdownHandler() ||
					this.deps.getExtensionErrorListener();
				if (hasBindings) {
					await newRunner.emit({ type: "session_start", reason: "reload" });
					await this.deps.extendResourcesFromExtensions("reload");
					this._doctorReloadRuntime();
				}
			} finally {
				offReloadErrors?.();
				offReloadErrors = undefined;
			}
			if (reloadErrors.length > 0) {
				throw new Error(`Extension reload failed doctor: ${reloadErrors.slice(0, 6).join("; ")}`);
			}
			await emitSessionShutdownEvent(previousRunner, { type: "session_shutdown", reason: "reload" });
			previousRunner.invalidate();
			await this.deps.getResourceLoader().commitReload?.();
			// Re-derive the memory subsystem from the reloaded settings/providers.
			await this.deps.initializeMemory();
			// This generation has fully committed (no rollback below this point), so any local
			// runtime the host no longer routes to under the new configuration can be stopped now.
			this.deps.reconcileLocalRuntimes?.();
		} catch (error) {
			offReloadErrors?.();
			if (newRunner && newRunner !== previousRunner) {
				newRunner.invalidate(
					"This extension ctx was discarded because reload failed and Pi restored the previous valid runtime.",
				);
			}
			await this.deps.getResourceLoader().rollbackReload?.();
			this._restoreReloadRuntimeSnapshot(snapshot);
			throw error;
		}
	}

	/**
	 * Unload a single extension without full reload.
	 * Runs the extension's session_shutdown lifecycle, unregisters its providers,
	 * disposes its event subscriptions, and rebuilds the runtime.
	 * Falls back to full reload on error.
	 */
	async unloadExtensionLive(extensionPath: string): Promise<void> {
		this._assertReloadQuiescent("unload extension");
		try {
			await this._unloadExtensionLive(extensionPath, true, true);
		} catch (error) {
			await this._recoverLiveExtensionFailure();
			throw error;
		}
	}

	private async _unloadExtensionLive(
		extensionPath: string,
		revokeExplicitGrant: boolean,
		notify: boolean,
	): Promise<void> {
		const ext = this.deps.getResourceLoader().getLoadedExtension(extensionPath);
		const explicitGrantPath = ext?.path ?? extensionPath;
		const hadExplicitGrant = this._explicitLiveExtensionPaths.has(explicitGrantPath);
		if (revokeExplicitGrant) {
			this._explicitLiveExtensionPaths.delete(explicitGrantPath);
		}
		if (!ext) {
			return;
		}

		const previousRunner = this.deps.getExtensionRunner();
		try {
			// Run session_shutdown lifecycle for this extension only
			await this.deps.getExtensionRunner().emitToExtension(ext, { type: "session_shutdown", reason: "unload" });

			// Unregister its providers (keyed by the extension's own path, as registered)
			const runtime = this.deps.getResourceLoader().getExtensions().runtime;
			const ownedMemoryProviders = runtime.memoryProvidersByExtension.get(ext.path) ?? new Set();
			const ownedContextMemoryProviders = runtime.contextMemoryProvidersByExtension.get(ext.path) ?? new Set();
			for (const name of runtime.getProvidersForExtension(ext.path)) {
				runtime.unregisterProvider(name, ext.path);
			}

			// Dispose its event subscriptions and run disposers
			await disposeExtensionEventSubscriptions([ext]);

			// Remove from loaded extensions
			this.deps.getResourceLoader().removeLoadedExtension(extensionPath);

			// Rebuild runtime with new extension set
			const activeToolNames = this.deps.getRequestedActiveToolNames() ?? this.deps.getActiveToolNames();
			const previousFlagValues = previousRunner.getFlagValues();
			this.buildRuntime({
				activeToolNames,
				flagValues: previousFlagValues,
				includeAllExtensionTools: true,
			});
			previousRunner.retire();
			const memorySnapshot = this.deps.createMemoryReloadSnapshot();
			this.deps.restoreMemoryReloadSnapshot({
				...memorySnapshot,
				pendingMemoryProviders: memorySnapshot.pendingMemoryProviders.filter(
					(provider) => !ownedMemoryProviders.has(provider),
				),
				pendingContextMemoryProviders: memorySnapshot.pendingContextMemoryProviders.filter(
					(provider) => !ownedContextMemoryProviders.has(provider),
				),
			});
			runtime.memoryProvidersByExtension.delete(ext.path);
			runtime.contextMemoryProvidersByExtension.delete(ext.path);
			await this.deps.initializeMemory();

			if (notify) {
				this.deps.notifyExtensionsChanged();
			}
		} catch (error) {
			if (revokeExplicitGrant && hadExplicitGrant) {
				this._explicitLiveExtensionPaths.add(explicitGrantPath);
			}
			throw error;
		}
	}

	/**
	 * Load a single extension without full reload.
	 * Loads the extension with fresh import, rebuilds the runtime,
	 * and runs the extension's session_start lifecycle.
	 * Falls back to full reload on error.
	 */
	async loadExtensionLive(extensionPath: string): Promise<void> {
		this._assertReloadQuiescent("load extension");
		this._assertExtensionLoadAllowed(extensionPath, "explicit");
		try {
			await this._loadExtensionLive(extensionPath, "explicit", true);
		} catch (error) {
			await this._recoverLiveExtensionFailure();
			throw error;
		}
	}

	private async _loadExtensionLive(
		extensionPath: string,
		authority: ExtensionImportAuthority,
		notify: boolean,
	): Promise<void> {
		const previousRunner = this.deps.getExtensionRunner();
		let explicitGrantPath: string | undefined;
		let hadExplicitGrant = false;
		try {
			// Load the extension with fresh import
			const extension = await this._importExtension(extensionPath);
			if (authority === "explicit") {
				explicitGrantPath = extension.path;
				hadExplicitGrant = this._explicitLiveExtensionPaths.has(explicitGrantPath);
				this._explicitLiveExtensionPaths.add(explicitGrantPath);
			}

			// Rebuild runtime to aggregate tools/commands/handlers/providers
			const activeToolNames = this.deps.getRequestedActiveToolNames() ?? this.deps.getActiveToolNames();
			const previousFlagValues = previousRunner.getFlagValues();
			this.buildRuntime({
				activeToolNames,
				flagValues: previousFlagValues,
				includeAllExtensionTools: true,
			});

			// Run session_start lifecycle for the new extension only
			await this.deps.getExtensionRunner().emitToExtension(extension, { type: "session_start", reason: "load" });
			// Activate newly registered legacy/context providers immediately. Reinitialization also
			// refreshes provider tools and preserves all providers owned by existing extensions.
			await this.deps.initializeMemory();
			previousRunner.retire();

			if (notify) {
				this.deps.notifyExtensionsChanged();
			}
		} catch (error) {
			if (explicitGrantPath && !hadExplicitGrant) {
				this._explicitLiveExtensionPaths.delete(explicitGrantPath);
			}
			throw error;
		}
	}

	private async _importExtension(extensionPath: string): Promise<Extension> {
		const { extension, error } = await this.deps.getResourceLoader().loadSingleExtension(extensionPath);
		if (error || !extension) {
			throw new Error(error || `Failed to load extension: ${extensionPath}`);
		}
		return extension;
	}

	private _assertExtensionLoadAllowed(extensionPath: string, authority: ExtensionImportAuthority): void {
		if (!this.deps.isExtensionPathAllowed(extensionPath, authority)) {
			throw new Error(`Cannot load extension outside the active resource profile: ${extensionPath}`);
		}
	}

	private async _recoverLiveExtensionFailure(): Promise<void> {
		try {
			await this.reload();
		} catch {
			// Preserve the original live-operation error; recovery is best effort.
		}
	}

	/**
	 * Reconcile loaded extensions with the active profile.
	 * Loads extensions that should be enabled but aren't, and unloads extensions that shouldn't be.
	 * Falls back to full reload if any individual load/unload fails.
	 */
	async reconcileLoadedExtensions(): Promise<void> {
		this._assertReloadQuiescent("reconcile extensions");

		try {
			// Get all discoverable extension paths
			const allDiscoverablePaths = await this.deps.getResourceLoader().getDiscoverableExtensionPaths();

			const loadedExtensions = this.deps.getResourceLoader().getExtensions().extensions;
			const targetAuthorities = new Map<string, "loaded" | ExtensionImportAuthority>();
			for (const extension of this.deps.filterExtensionsForRuntime(
				loadedExtensions,
				this._explicitLiveExtensionPaths,
			)) {
				targetAuthorities.set(extension.path, "loaded");
			}
			for (const path of this._explicitLiveExtensionPaths) {
				if (this.deps.isExtensionPathAllowed(path, "explicit")) {
					targetAuthorities.set(path, "explicit");
				}
			}
			for (const path of allDiscoverablePaths) {
				if (this.deps.isExtensionPathAllowed(path, "profile") && !targetAuthorities.has(path)) {
					targetAuthorities.set(path, "profile");
				}
			}

			// Apply unloads first, then loads. A failed mutation stops the generation immediately; the
			// outer recovery reload restores one coherent runtime instead of compounding partial changes.
			for (const extension of [...loadedExtensions]) {
				if (targetAuthorities.has(extension.path) || targetAuthorities.has(extension.resolvedPath)) continue;
				this._assertReloadQuiescent("reconcile extensions");
				await this._unloadExtensionLive(extension.path, false, false);
			}

			for (const [path, authority] of targetAuthorities) {
				if (authority === "loaded" || this.deps.getResourceLoader().getLoadedExtension(path)) continue;
				this._assertReloadQuiescent("reconcile extensions");
				this._assertExtensionLoadAllowed(path, authority);
				await this._loadExtensionLive(path, authority, false);
			}

			// Single notification at the end
			this.deps.notifyExtensionsChanged();
		} catch (error) {
			await this._recoverLiveExtensionFailure();
			throw error;
		}
	}
}

export async function resolveScoutModel(
	modelRegistry: ModelRegistry,
	modelSetting: string,
	agentDir: string,
	isModelExhausted: (model: Model<Api>) => boolean = () => false,
	textProtocolOverride?: boolean,
) {
	const model =
		modelSetting === "auto"
			? findFastContextModel(modelRegistry)
			: resolveCliModel({ cliModel: modelSetting, modelRegistry }).model;
	if (!model) {
		return { failure: `no scout model matched ${modelSetting}` };
	}
	if (isModelExhausted(model)) {
		return { failure: `${model.provider}/${model.id} exhausted: quota` };
	}
	const modelRef = `${model.provider}/${model.id}`;
	const adaptation = ModelAdaptationStore.forAgentDir(agentDir).get(modelRef);
	const protocolResolution = resolveModelToolProtocol({
		model,
		settingsOverride: textProtocolOverride,
		adaptation,
	});
	if (modelSetting === "auto") {
		const fitness = FitnessStore.forAgentDir(agentDir)
			.getForHost()
			.find((entry) => entry.model === modelRef);
		const verdict = evaluateSurfaceFitness("scout_auto", fitness?.report);
		if (!verdict.fit) {
			return verdict.reason === "unprobed"
				? { failure: `${modelRef} unprobed — run /fitness before auto-selection` }
				: { failure: `${modelRef} unfit (${verdict.lane} ${verdict.succeeded}/${verdict.total})` };
		}
		const toolProbe = adaptation.toolProbe;
		if (!toolProbe) {
			return { failure: `${modelRef} unprobed — run /toolprobe before auto-selection` };
		}
		if (toolProbe.status === "none") {
			return { failure: `${modelRef} unfit (real tool execution: none)` };
		}
		if (toolProbe.status === "native" && toolProbe.nativeGrade !== "task") {
			return {
				failure: `${modelRef} unfit (real tool execution: native ${toolProbe.nativeGrade ?? "ungraded"})`,
			};
		}
		if (toolProbe.status === "text-protocol" && !protocolResolution.protocol) {
			return { failure: `${modelRef} unfit (real tool execution: phone calibration unavailable)` };
		}
	}
	const auth = await modelRegistry.getApiKeyAndHeaders(model);
	if (!auth.ok) {
		return { failure: auth.error ?? `no usable auth for scout model ${model.provider}/${model.id}` };
	}
	return {
		model,
		apiKey: auth.apiKey,
		headers: auth.headers,
		textToolCallProtocol: protocolResolution.protocol,
	};
}

function findFastContextModel(modelRegistry: ModelRegistry) {
	return modelRegistry.getAll().find((model) => {
		const text = `${model.provider}/${model.id} ${model.name}`.toLowerCase();
		return text.includes("fastcontext");
	});
}

function resolveCwdPath(cwd: string, path: string): string {
	return isAbsolute(path) ? path : join(cwd, path);
}

function countFileLines(path: string): number | undefined {
	try {
		return countFileLinesSync(path, "Context scout citation file");
	} catch {
		return undefined;
	}
}
